import { mapKeys, mapValues, set } from 'lodash'
import type { AppSpecData, ViewerClientSpecMap } from '@wix/thunderbolt-symbols'
import type { Component, RefSchemas, Structure, FetchFn } from '@wix/thunderbolt-becky-types'
import type { BuilderLegacyComponentsMap } from '@wix/thunderbolt-commons'
import type { RefInfo } from '@wix/document-services-types'

export type BuilderRefInfo = Omit<RefInfo, 'isList' | 'isRefOwner' | 'isRelationalSplit' | 'dontSerialize'> & {
	list: boolean
	refOwner: boolean
}

export type BuilderRefSchemas = {
	[map: string]: {
		[dataType: string]: {
			references: Array<BuilderRefInfo>
		}
	}
}

export const getBuilderComponents = (clientSpecMap: ViewerClientSpecMap): Set<string> => {
	const builderComponents = new Set<string>()
	Object.values(clientSpecMap).forEach((app: AppSpecData) => {
		app.components?.forEach((comp) => {
			if (comp.type === 'EDITOR_REACT_COMPONENT') {
				builderComponents.add(comp.data.type)
			}
		})
	})
	return builderComponents
}

export const fixBuilderComponent = (component: Component) => {
	set(component, 'builderType', component.componentType)
	set(component, 'componentType', 'Builder')
}

export const fixBuilderLegacyComponent = (component: Component, refType: string) => {
	set(component, 'componentType', refType)
}

export const fixStructureWithBuilderComponents = (structure: Structure, builderComponents: Set<string>) => {
	for (const compId in structure) {
		const comp = structure[compId]
		const isBuilderComponent = comp && builderComponents.has(comp.componentType)
		if (isBuilderComponent) {
			fixBuilderComponent(comp)
		}
	}
	return structure
}

export const fixStructureWithBuilderLegacyComponents = (
	structure: Structure,
	builderLegacyComponentsMap: BuilderLegacyComponentsMap
) => {
	for (const compId in structure) {
		const comp = structure[compId]
		const refType = builderLegacyComponentsMap[comp.componentType]
		if (refType !== undefined) {
			fixBuilderLegacyComponent(comp, refType)
		}
	}

	return structure
}

export const fixMapNames = (refSchemas: RefSchemas): RefSchemas => {
	return mapKeys(refSchemas, (_value, key) => {
		switch (key) {
			case 'data':
				return 'document_data'
			case 'style':
				return 'theme_data'
			default:
				return key
		}
	})
}

export const builderRefInfoToRefInfo = (builderRefInfo: BuilderRefInfo): RefInfo => {
	const { list, refOwner, ...rest } = builderRefInfo
	return {
		...rest,
		isList: list,
		isRefOwner: refOwner,
	}
}

export const builderRefSchemasToRefSchemas = (refSchemas: BuilderRefSchemas): RefSchemas => {
	return mapValues(refSchemas, (map) => {
		return mapValues(map, (dataTypeObj) => {
			// DM sets without "references", fetch returns with them
			return (dataTypeObj.references || dataTypeObj).map(builderRefInfoToRefInfo)
		})
	})
}

export const getBuilderComponentsAppDefIds = (clientSpecMap: ViewerClientSpecMap): Array<string> =>
	Object.values(clientSpecMap)
		.filter((app: AppSpecData) => app.components?.some((comp) => comp.type === 'EDITOR_REACT_COMPONENT'))
		.map((app: AppSpecData) => app.appDefinitionId)

export const getRefsForAppDefId = async (_appDefId: string, fetchFn: FetchFn): Promise<RefSchemas> => {
	try {
		const randomQueryParam = Math.random().toString(36).substring(2, 9)
		const response = await fetchFn(
			`https://viewer-assets.parastorage.com/manifests/${_appDefId}/ref-resolved-manifest.json?randomQueryParam=${randomQueryParam}`
		)
		const data = (await response.json()) as BuilderRefSchemas
		return fixMapNames(builderRefSchemasToRefSchemas(data))
	} catch (error) {
		console.error(`Failed to fetch ref schemas for appDefId: ${_appDefId}`, error)
		return {}
	}
}

export const mergeRefs = (appDefIdToRefs: Record<string, RefSchemas>): RefSchemas =>
	Object.values(appDefIdToRefs).reduce((acc, refs) => {
		return Object.entries(refs).reduce((accMap, [mapName, mapRefs]) => {
			const existingMapRefs = accMap[mapName] || {}
			const mergedDataTypes = Object.entries(mapRefs).reduce((accDataTypes, [dataType, refInfoArray]) => {
				const existingRefInfoArray = existingMapRefs[dataType] || []
				return {
					...accDataTypes,
					[dataType]: [...existingRefInfoArray, ...refInfoArray],
				}
			}, existingMapRefs)

			return {
				...accMap,
				[mapName]: mergedDataTypes,
			}
		}, acc)
	}, {} as RefSchemas)

export const getBuilderComponentRefPromises = (
	builderComponentsAppDefIds: Array<string>,
	fetchFn: FetchFn
): Array<{ appDefId: string; refsPromise: Promise<RefSchemas> }> => {
	return builderComponentsAppDefIds.map((appDefId) => {
		return { appDefId, refsPromise: getRefsForAppDefId(appDefId, fetchFn) }
	})
}

export const formatToAppDefIdToRefs = (
	results: Array<{ appDefId: string; refs: RefSchemas }>
): Record<string, RefSchemas> => {
	return results.reduce((acc, { appDefId, refs }) => {
		acc[appDefId] = refs
		return acc
	}, {} as Record<string, RefSchemas>)
}

export const getBuilderComponentRefSchemas = async (
	clientSpecMap: ViewerClientSpecMap,
	fetchFn: FetchFn
): Promise<Record<string, RefSchemas>> => {
	const builderComponentsAppDefIds = getBuilderComponentsAppDefIds(clientSpecMap)
	const refPromises = getBuilderComponentRefPromises(builderComponentsAppDefIds, fetchFn)

	const results = await Promise.all(
		refPromises.map(async ({ appDefId, refsPromise }) => ({ appDefId, refs: await refsPromise }))
	)

	return formatToAppDefIdToRefs(results)
}

export const formatBuilderRefSchemasRecord = (
	builderRefSchemasByAppDefId: Record<string, BuilderRefSchemas>
): Record<string, RefSchemas> => {
	return Object.entries(builderRefSchemasByAppDefId).reduce((acc, [appDefId, builderRefSchemas]) => {
		acc[appDefId] = fixMapNames(builderRefSchemasToRefSchemas(builderRefSchemas))
		return acc
	}, {} as Record<string, RefSchemas>)
}
